// Building blocks shared by the agents tabs (decision D20): permissions, origin and status badges, score
// summaries. Every value shown is a server fact; an absent score says why (no runs, insufficient sample).
import type { AgentOrigin, AgentRegistryEntry, LearningStatus } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { Pill } from "../../components/Status.tsx";
import type { IconKind, Tone } from "../../lib/status.ts";
import type { ScorecardItem } from "../../api/endpoints-agents.ts";
import { useOrgRole } from "../config/shared.tsx";

/** What the signed-in person may do here (the API enforces it; this only avoids dead ends). */
export function useAgentPermissions(): { canReview: boolean; isAdmin: boolean; canWriteLearnings: boolean } {
  const { atLeast } = useOrgRole();
  return { canReview: atLeast("maintainer"), isAdmin: atLeast("admin"), canWriteLearnings: atLeast("maintainer") };
}

export function OriginBadge({ origin }: { origin: AgentOrigin }) {
  const { t } = useI18n();
  return (
    <span className={`badge${origin === "platform" ? " badge--accent" : ""}`} data-testid="agent-origin" data-origin={origin}>
      {t(`agents.origin.${origin}`)}
    </span>
  );
}

const AGENT_STATE_VIEW: Record<string, { tone: Tone; icon: IconKind }> = {
  active: { tone: "success", icon: "check" },
  disabled: { tone: "neutral", icon: "ban" },
  draft: { tone: "warning", icon: "circle" },
};

export function AgentStatePill({ state }: { state: string }) {
  const { t } = useI18n();
  const view = AGENT_STATE_VIEW[state] ?? { tone: "info" as Tone, icon: "circle" as IconKind };
  const label = state === "active" || state === "disabled" || state === "draft" ? t(`state.${state}`) : state;
  return (
    <Pill tone={view.tone} icon={view.icon}>
      {label}
    </Pill>
  );
}

export const LEARNING_STATUS_VIEW: Record<LearningStatus, { tone: Tone; icon: IconKind }> = {
  proposed: { tone: "warning", icon: "clock" },
  approved: { tone: "success", icon: "check" },
  rejected: { tone: "danger", icon: "x" },
  deprecated: { tone: "neutral", icon: "ban" },
  superseded: { tone: "neutral", icon: "circle" },
};

export function LearningStatusPill({ status }: { status: LearningStatus }) {
  const { t } = useI18n();
  const view = LEARNING_STATUS_VIEW[status];
  return (
    <Pill tone={view.tone} icon={view.icon}>
      {t(`agents.status.${status}`)}
    </Pill>
  );
}

export function formatRatio(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(value);
}

export function formatBound(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

/**
 * Score of one agent version: the 0–100 score with its Wilson lower bound and sample size, or the reason it is
 * absent (no runs in the window, or fewer runs than the agent's minimum sample).
 */
export function ScoreSummary({ item, windowDays, compact = false }: { item: ScorecardItem | undefined; windowDays: number; compact?: boolean }) {
  const { t, locale } = useI18n();
  if (!item || item.runs === 0) {
    return (
      <span className="agent-score agent-score--none subtle" data-testid="agent-score" data-state="none">
        {t("agents.score.none", { days: windowDays })}
      </span>
    );
  }
  if (item.score === null) {
    return (
      <span className="agent-score agent-score--insufficient" data-testid="agent-score" data-state="insufficient">
        {t("agents.score.insufficient", { runs: item.runs, min: item.min_sample })}
      </span>
    );
  }
  const tone = item.score >= 70 ? "success" : item.score >= 50 ? "warning" : "danger";
  return (
    <span className={`agent-score agent-score--${tone}`} data-testid="agent-score" data-state="scored">
      <strong className="agent-score-value">{t("agents.score.value", { score: Math.round(item.score) })}</strong>
      {compact ? null : <span className="subtle">{t("agents.score.detail", { bound: formatBound(item.success_lower_bound, locale), runs: item.runs })}</span>}
    </span>
  );
}

/** Score of the version the agent runs now (falls back to its most recent scored version). */
export function currentScore(agent: AgentRegistryEntry, items: readonly ScorecardItem[]): ScorecardItem | undefined {
  const own = items.filter((s) => s.agent_profile_id === agent.id);
  return own.find((s) => s.agent_profile_version_id === (agent.current_version?.id ?? null)) ?? own.sort((a, b) => (b.version_number ?? 0) - (a.version_number ?? 0))[0];
}
